import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Messages() {
  const { user, loading } = useAuth();
  const { t, lang } = useLang();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");

  const loadThreads = useCallback(async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    if (data.length && !active) setActive(data[0]);
  }, [active]);

  const loadThread = async (t) => {
    setActive(t);
    const { data } = await api.get(`/messages/thread/${t.thread_id}`);
    setMessages(data);
  };

  useEffect(() => {
    if (user) loadThreads();
    // Deliberately only refire when the user id changes — `loadThreads` closes
    // over `active` but we do NOT want to refetch every time the active thread
    // switches (that's what `loadThread` is for below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (active) loadThread(active); }, [active?.thread_id]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    await api.post("/messages", { to_user_id: active.other_user.id, body, thread_id: active.thread_id });
    setBody("");
    loadThread(active);
  };

  if (loading) return null;
  if (!user) return <Navigate to="/connexion"/>;

  const locale = lang === "en" ? "en-GB" : "fr-FR";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="messages-page">
      <h1 className="heading font-extrabold text-4xl mb-8">{t("msg.title")}</h1>
      <div className="grid lg:grid-cols-[320px_1fr] gap-6 h-[70vh]">
        <div className="bg-white border border-[#1A3322]/10 rounded-lg overflow-y-auto" data-testid="threads-list">
          {threads.length === 0 && <p className="p-6 text-[#4B5563] text-sm">{t("msg.no_conv")}</p>}
          {threads.map((th) => (
            <button key={th.thread_id} onClick={() => setActive(th)}
              className={`w-full text-left p-4 border-b border-[#1A3322]/5 hover:bg-[#F5F5F0] ${active?.thread_id === th.thread_id ? "bg-[#F5F5F0]" : ""}`}
              data-testid={`thread-${th.thread_id}`}>
              <p className="font-semibold text-sm">{th.other_user?.full_name || t("msg.user")}</p>
              <p className="text-xs text-[#4B5563] truncate mt-1">{th.last_message}</p>
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#1A3322]/10 rounded-lg flex flex-col">
          {active ? (
            <>
              <div className="p-4 border-b border-[#1A3322]/10 font-semibold">{active.other_user?.full_name}</div>
              <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="messages-list">
                {messages.map((m) => (
                  <div key={m.id} className={`max-w-[70%] px-4 py-2 rounded-lg ${m.from_user_id === user.id ? "bg-[#1A3322] text-white ml-auto" : "bg-[#F5F5F0]"}`} data-testid={`msg-${m.id}`}>
                    {m.body}
                    <p className={`text-[10px] mt-1 ${m.from_user_id === user.id ? "text-white/60" : "text-[#4B5563]"}`}>{new Date(m.created_at).toLocaleTimeString(locale,{hour:"2-digit",minute:"2-digit"})}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="p-4 border-t border-[#1A3322]/10 flex gap-2">
                <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("msg.placeholder")} data-testid="message-input"/>
                <Button type="submit" className="pill-btn bg-[#1A3322] rounded-full" data-testid="message-send">{t("msg.send")}</Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[#4B5563]">{t("msg.select")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
